import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const PricingDashboard = ({ onClose }) => {
  const [pricingData, setPricingData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { key: 'all', label: 'All Categories', icon: '🎯' },
    { key: 'entry_ticket', label: 'Entry Tickets', icon: '🌳' },
    { key: 'parking_ticket', label: 'Parking', icon: '🅿️' },
    { key: 'chappal_stand_ticket', label: 'Chappal Stand', icon: '👟' },
    { key: 'puja_ticket', label: 'Puja Services', icon: '🙏' },
    { key: 'event_ticket', label: 'Events', icon: '🎭' },
    { key: 'other', label: 'Other', icon: '🏛️' }
  ];

  useEffect(() => {
    fetchPricingData();
  }, []);

  const fetchPricingData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/test-tickets`);
      const tickets = response.data.tickets || [];
      
      const categoryData = {};
      categories.slice(1).forEach(cat => {
        const categoryTickets = tickets.filter(t => t.category === cat.key);
        categoryData[cat.key] = {
          tickets: categoryTickets,
          count: categoryTickets.length,
          avgPrice: categoryTickets.length > 0 ? 
            categoryTickets.reduce((sum, t) => sum + t.price, 0) / categoryTickets.length : 0,
          minPrice: categoryTickets.length > 0 ? Math.min(...categoryTickets.map(t => t.price)) : 0,
          maxPrice: categoryTickets.length > 0 ? Math.max(...categoryTickets.map(t => t.price)) : 0,
          totalRevenue: categoryTickets.reduce((sum, t) => sum + (t.price * (t.bookings || 0)), 0)
        };
      });
      
      setPricingData(categoryData);
    } catch (error) {
      console.error('Error fetching pricing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedData = () => {
    if (selectedCategory === 'all') {
      const allTickets = Object.values(pricingData).flatMap(cat => cat.tickets);
      return {
        tickets: allTickets,
        count: allTickets.length,
        avgPrice: allTickets.length > 0 ? 
          allTickets.reduce((sum, t) => sum + t.price, 0) / allTickets.length : 0,
        minPrice: allTickets.length > 0 ? Math.min(...allTickets.map(t => t.price)) : 0,
        maxPrice: allTickets.length > 0 ? Math.max(...allTickets.map(t => t.price)) : 0
      };
    }
    return pricingData[selectedCategory] || { tickets: [], count: 0, avgPrice: 0, minPrice: 0, maxPrice: 0 };
  };

  const selectedData = getSelectedData();
  const selectedCategoryInfo = categories.find(c => c.key === selectedCategory);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, color: '#2d3748', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px' }}>💰</span>
            Pricing Dashboard
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#718096'
            }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px' }}>⏳ Loading pricing data...</div>
          </div>
        ) : (
          <>
            {/* Category Selector */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {categories.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setSelectedCategory(cat.key)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      background: selectedCategory === cat.key 
                        ? 'linear-gradient(45deg, #3b82f6, #8b5cf6)' 
                        : '#f8fafc',
                      color: selectedCategory === cat.key ? 'white' : '#4a5568',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>{cat.icon}</span>
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Analytics Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '32px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                padding: '20px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{selectedCategoryInfo?.icon}</div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>{selectedData.count}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Tickets</div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #48bb78, #38a169)',
                color: 'white',
                padding: '20px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>💵</div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>₹{selectedData.avgPrice.toFixed(0)}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Average Price</div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                color: 'white',
                padding: '20px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📊</div>
                <div style={{ fontSize: '18px', fontWeight: '700' }}>₹{selectedData.minPrice} - ₹{selectedData.maxPrice}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Price Range</div>
              </div>
            </div>

            {/* Tickets Table */}
            <div style={{
              background: '#f8fafc',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#2d3748' }}>
                {selectedCategoryInfo?.label} Details
              </h3>
              
              {selectedData.tickets.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '12px', color: '#4a5568', fontWeight: '600' }}>Site</th>
                        <th style={{ textAlign: 'left', padding: '12px', color: '#4a5568', fontWeight: '600' }}>City</th>
                        <th style={{ textAlign: 'right', padding: '12px', color: '#4a5568', fontWeight: '600' }}>Price</th>
                        <th style={{ textAlign: 'center', padding: '12px', color: '#4a5568', fontWeight: '600' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedData.tickets.map(ticket => (
                        <tr key={ticket._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '12px', color: '#2d3748' }}>{ticket.name}</td>
                          <td style={{ padding: '12px', color: '#718096' }}>{ticket.city}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#48bb78' }}>
                            ₹{ticket.price}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              background: ticket.isActive ? '#d4edda' : '#f8d7da',
                              color: ticket.isActive ? '#155724' : '#721c24',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              {ticket.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                  No tickets found for {selectedCategoryInfo?.label.toLowerCase()}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PricingDashboard;